// SPDX-License-Identifier: Apache-2.0

import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppContext";
import GetLPPositionsResponse from "../../api/GetLPPositionsResponse";
import {
  CKB_DECIMALS,
  lpFormatShares,
  lpToHuman,
  lpPoolSharePct,
  lpTruncateAddress,
} from "../../constants/lpUnits";
import { isNotConfigured, mapLPError } from "./lpErrors";
import CopyButton from "./CopyButton";

// Shows the connected LP's CKB cells and ETH pool position via GetLPPositions.
function LPPositions() {
  const { t } = useTranslation();
  const ctx = useContext(AppContext);

  const [positions, setPositions] = useState<GetLPPositionsResponse>();
  const [loading, setLoading] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const [error, setError] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    setNotConfigured(false);
    try {
      const ethOwner = ctx.client.ethAddress?.toString() ?? "";
      const ownerLockHash =
        (await ctx.client.ckbProvider?.getOwnerLockHash()) ?? "";
      const resp = await ctx.client.getLPPositions(ownerLockHash, ethOwner);
      setPositions(resp);
    } catch (err) {
      if (isNotConfigured(err)) {
        setNotConfigured(true);
      } else {
        setError(mapLPError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [ctx.client]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="w-100">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">{t("lpPositions")}</h5>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={load}
          disabled={loading}
        >
          {loading ? t("lpLoading") : t("lpRefresh")}
        </Button>
      </div>

      {loading && !positions && <p>{t("lpLoading")}</p>}
      {notConfigured && <p>{t("lpNotConfigured")}</p>}
      {error && (
        <span className="invalid-feedback" style={{ display: "block" }}>
          {error}
        </span>
      )}

      {positions && !notConfigured && (
        <>
          <h6 className="mt-3">{t("lpYourCkbCells")}</h6>
          {positions.ckbCells.length === 0 ? (
            <p>{t("lpNoCkbCells")}</p>
          ) : (
            <Table size="sm" responsive>
              <thead>
                <tr>
                  <th title={t("lpCellTip")}>{t("lpCell")}</th>
                  <th title={t("lpAvailableTip")}>{t("lpAvailable")}</th>
                  <th title={t("lpReservedTip")}>{t("lpReserved")}</th>
                  <th title={t("lpFeesEarnedTip")}>{t("lpFeesEarned")}</th>
                  <th title={t("lpFeeRateTip")}>{t("lpFeeRate")}</th>
                  <th title={t("lpBeneficiaryTip")}>{t("lpBeneficiary")}</th>
                  <th>{t("lpActive")}</th>
                </tr>
              </thead>
              <tbody>
                {positions.ckbCells.map((cell) => (
                  <tr key={cell.outpoint}>
                    <td title={cell.outpoint}>
                      <code>{lpTruncateAddress(cell.outpoint)}</code>
                      <CopyButton value={cell.outpoint} />
                    </td>
                    <td>{lpToHuman(cell.availableCKB, CKB_DECIMALS)} CKB</td>
                    <td>{lpToHuman(cell.reservedCKB, CKB_DECIMALS)} CKB</td>
                    <td>
                      {lpToHuman(cell.cumulativeFeesEarnedCKB, CKB_DECIMALS)}{" "}
                      CKB
                    </td>
                    <td>{(cell.policy.feeRateBps / 100).toString()}%</td>
                    {/* Where this cell's converted CKB earns ETH-pool shares.
                        Shown in full via title so an owner can verify it
                        against their own wallet — it is frozen on-chain. */}
                    <td title={cell.ethBeneficiary}>
                      <code>{lpTruncateAddress(cell.ethBeneficiary)}</code>
                      <CopyButton value={cell.ethBeneficiary} />
                    </td>
                    <td>{cell.active ? "✓" : "✗"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {positions.ckbCells.length > 0 && (
            <p className="text-muted" style={{ fontSize: "0.875em" }}>
              {t("lpPositionsCkbNote")}
            </p>
          )}

          <h6 className="mt-3">{t("lpYourEthPosition")}</h6>
          <Table size="sm" responsive>
            <tbody>
              <tr>
                {/* Scaled to 1e18 (1 share per wei at pool launch); the raw
                    wire value is in the title and copyable. */}
                <td>{t("lpShares")}</td>
                <td title={positions.ethShares}>
                  {lpFormatShares(positions.ethShares)}
                  <CopyButton value={positions.ethShares} />
                </td>
              </tr>
              <tr>
                <td title={t("lpWithdrawableTip")}>{t("lpWithdrawable")}</td>
                <td>{lpToHuman(positions.ethWithdrawable, 18)} ETH</td>
              </tr>
              <tr>
                <td>{t("lpPoolShare")}</td>
                <td>
                  {lpPoolSharePct(
                    positions.ethShares,
                    positions.ethTotalShares,
                  )}
                  %
                </td>
              </tr>
            </tbody>
          </Table>
          <p className="text-muted" style={{ fontSize: "0.875em" }}>
            {t("lpSharesNote")}
          </p>
        </>
      )}
    </div>
  );
}

export default LPPositions;
